import {
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import { SxProps, Theme } from "@mui/material/styles";
import TeamReportDataTableCell from "../TeamReportDataTableCell";
import ShotDistributionBar from "../../ShotDistributionBar";
import {
  commonRenderValue,
  getReadableRowLabel,
  hasNoCounts,
  isBelowSampleFloor,
  isLowSampleCell,
  isSmallSampleRow,
  perChancePPPText,
  signedDecimals,
} from "../utils";
import {
  getColumnGroupStarts,
  TRANS_RATE_BLANK_ROWS,
  FREQUENCY_HEADERS,
  HYBRID_NUMERAL_HEADERS,
  LOW_SAMPLE_ROW_OPACITY,
  ONE_DECIMAL_HEADERS,
  PER_CHANCE_PPP_HEADERS,
  ROW_STRIPE_COLOR,
  TWO_DECIMAL_HEADERS,
  COUNT_HEADERS,
  CELL_SAMPLE_FLOORS,
  EXPECTED_COUNT_HEADERS,
} from "../../constants";
import { TeamReportDataTableRow } from "../types";
import { ShotQueryContext } from "../../ShotDetailModal/types";
import { pickTypeLabel } from "../../pickTypes";
import { PickTypeLabel } from "../../PickTypeDiagram";
import { formatSigned } from "../../../util/formatSigned";

// A game's rebounds and, beside them, the number expected when the shot went up: "4 (3.1)". The expectation is
// context, so it reads smaller and grey; inline so the table's body type rule can't resize it.
const expectedCountText = (value: number, expected?: number): React.ReactNode => (
  <>
    {Number(value).toLocaleString()}
    {expected !== undefined && expected !== null && !isNaN(Number(expected)) && (
      <>
        {" "}
        <span
          className="ExpectedCount"
          style={{ fontSize: "0.85em", fontWeight: 400, color: "rgba(0, 0, 0, 0.6)" }}
        >
          ({Number(expected).toFixed(1)})
        </span>
      </>
    )}
  </>
);

export default function TeamReportDataRow({
  row,
  headers,
  isSubRow,
  isExpanded,
  subRowStyles,
  showShotDistributionColumn,
  shotDistributionContext,
  compactRowLabels,
  hideRowLabels,
  onToggle,
  onShotDistributionClick,
  onVideoPlaylistClick,
  videoLoadingKey,
  parentRowName,
  striped,
  valueOnlyColumns,
  perChancePPP,
  separatorBelow,
  disableSmallSampleFade,
  extraNumeralHeaders,
  needleOrdinalColumns,
  smallSampleFloor,
  pickTypeRow,
  singleGame,
}: {
  row: TeamReportDataTableRow;
  headers: string[];
  isSubRow?: boolean;
  isExpanded: boolean;
  subRowStyles: SxProps<Theme>;
  showShotDistributionColumn: boolean;
  shotDistributionContext?: "offense" | "defense";
  compactRowLabels?: boolean;
  hideRowLabels?: boolean;
  /** Zebra stripe this row (parent + its sub-rows share the parent's value) */
  striped?: boolean;
  /** Columns that never carry a pill — center the value, no pill spacer */
  valueOnlyColumns?: Set<string>;
  /** Display chance-level PPP columns per-chance (1.08, not 108.2) */
  perChancePPP?: boolean;
  onToggle?: () => void;
  onShotDistributionClick?: (
    queryContext: ShotQueryContext,
    rowName?: string,
    parentRowName?: string,
  ) => void;
  onVideoPlaylistClick?: (
    queryContext: ShotQueryContext,
    rowName?: string,
    parentRowName?: string,
    metaKey?: boolean,
  ) => void;
  /** Key identifying which row's video is currently loading */
  videoLoadingKey?: string | null;
  /** Parent row name for subrows, used in modal titles */
  parentRowName?: string;
  /** Draw a group divider under this row (the overview double-rule
   * treatment — e.g. under an All lead row, or the coverage-family seam) */
  separatorBelow?: boolean;
  /** Skip the low-sample row fade (condensed zone tables) */
  disableSmallSampleFade?: boolean;
  /** Per-table additions to the hybrid-numeral columns (e.g. Avg Crashers /
   * ORB% on the reb tables, where they're headline process metrics) */
  extraNumeralHeaders?: Set<string>;
  /** Frequency columns that show the rank ordinal beside the needle */
  needleOrdinalColumns?: Set<string>;
  /** Rows under this many possessions grey out and lose their chips */
  smallSampleFloor?: number;
  /** The row name is a PickType value: show its display name (pickTypes.ts) */
  pickTypeRow?: boolean;
  /** A game report's row: the rebounding value columns are counts (1 decimal) */
  singleGame?: boolean;
}): JSX.Element {
  const pickTypeValue =
    pickTypeRow && typeof row.name === "string" ? row.name : undefined;
  const label =
    pickTypeValue !== undefined
      ? pickTypeLabel(pickTypeValue)
      : getReadableRowLabel(row.name, compactRowLabels);
  const labelStr = typeof label === "string" ? label : undefined;
  const disabled = hasNoCounts(row.cells);
  const belowFloor = isBelowSampleFloor(row.cells, smallSampleFloor);
  const smallSample =
    belowFloor || (!disableSmallSampleFade && isSmallSampleRow(row.cells));
  const columnGroupStarts = getColumnGroupStarts(headers);

  return (
    <TableRow
      className={
        [
          smallSample && "TeamReportLowSampleRow",
          striped && "TeamReportStripedRow",
          // Styled from the parent Table's sx — a row-level border override
          // loses the specificity race against the table-wide hairline rule.
          separatorBelow && "TeamReportSeparatorRow",
        ]
          .filter(Boolean)
          .join(" ") || undefined
      }
      sx={{
        ...(isSubRow ? (subRowStyles as Record<string, unknown>) : {}),
        ...(striped ? { backgroundColor: ROW_STRIPE_COLOR } : {}),
        ...(smallSample ? { opacity: LOW_SAMPLE_ROW_OPACITY } : {}),
      }}
    >
      {!hideRowLabels && (
        // py must match the data cells (MUI small default is 6px — it was
        // silently setting the row height)
        <TableCell sx={{ px: 0.5, py: 0.25, pl: isSubRow ? 4 : 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {!isSubRow && onToggle ? (
              <IconButton size="small" onClick={onToggle} sx={{ p: 0 }}>
                {isExpanded ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: "0.949rem" }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: "0.949rem" }} />
                )}
              </IconButton>
            ) : (
              !isSubRow && <Stack sx={{ width: "0.825rem" }} />
            )}
            {/* a Type table's row shows its PickType's court sketch on hover (Jacob, 2026-10-07; screen only) */}
            {(() => {
              const text = (
                <Typography
                  onClick={onToggle}
                  color={disabled ? "text.disabled" : "inherit"}
                  sx={{
                    whiteSpace: "nowrap",
                    ...(onToggle && { cursor: "pointer" }),
                  }}
                >
                  {label}
                </Typography>
              );
              return pickTypeValue !== undefined ? (
                <PickTypeLabel value={pickTypeValue}>{text}</PickTypeLabel>
              ) : (
                text
              );
            })()}
          </Stack>
        </TableCell>
      )}
      {headers.map((col, cellIndex) => {
        // Trans Rate is only meaningful on runnable possession starts —
        // the non-runnable start-type rows show a blank cell in that column.
        // Likewise the share on a class table's All lead row: the row IS the
        // denominator, so its constant 100% renders blank ("% of Actions"
        // since TR5).
        const blankCell =
          (col === "Trans Rate" &&
            typeof row.name === "string" &&
            TRANS_RATE_BLANK_ROWS.has(row.name)) ||
          ((col === "% of Poss" || col === "% of Actions") &&
            typeof row.name === "string" &&
            row.name.startsWith("All "));
        const cellData = blankCell ? undefined : row.cells[col];
        const { value, subValue, rank, percentile, rankPopulation, parts } =
          cellData || {};
        const lowSample = isLowSampleCell(col, cellData);
        const expectedCount = EXPECTED_COUNT_HEADERS.has(col);
        const signed = signedDecimals(col, singleGame);
        const renderValue =
          expectedCount
            ? (value?: number) =>
                value === undefined || value === null || isNaN(Number(value))
                  ? value
                  : expectedCountText(value, subValue)
            : COUNT_HEADERS.has(col)
            ? (value?: number) => value?.toLocaleString()
            : perChancePPP && PER_CHANCE_PPP_HEADERS.has(col)
            ? (value?: number) =>
                value === undefined || value === null || isNaN(Number(value))
                  ? value
                  : perChancePPPText(value, col)
            : signed !== undefined
            ? // +/-, impact, expectation and value columns: "+1.2" / "-0.4" / "0.0" (RV1)
              (value?: number) =>
                value === undefined || value === null || isNaN(Number(value))
                  ? value
                  : formatSigned(Number(value), signed)
            : TWO_DECIMAL_HEADERS.has(col)
            ? // Already-per-unit point rates (Pts/ORB, Pts/Miss): two
              // decimals, no ÷100 — the backend emits these frac-scaled.
              (value?: number) =>
                value === undefined || value === null || isNaN(Number(value))
                  ? value
                  : Number(value).toFixed(2)
            : ONE_DECIMAL_HEADERS.has(col)
            ? // Shares/rates read as one decimal everywhere ("4.3", not "4.30")
              (value?: number) =>
                value === undefined || value === null || isNaN(Number(value))
                  ? value
                  : Number(value).toFixed(1)
            : commonRenderValue;
        return (
          <TeamReportDataTableCell
            key={cellIndex}
            value={value}
            // the "(exp)" cells print their expectation inside the value
            subValue={expectedCount ? undefined : subValue}
            rank={rank}
            percentile={percentile}
            rankPopulation={rankPopulation}
            renderValue={renderValue}
            isDisabled={disabled}
            isFrequency={FREQUENCY_HEADERS.has(col)}
            groupStart={columnGroupStarts.has(col)}
            // a game's rebounds are the table's content, not its sample: full ink, though they carry no chip
            valueOnly={valueOnlyColumns?.has(col) && !expectedCount}
            hybridNumeral={
              HYBRID_NUMERAL_HEADERS.has(col) ||
              Boolean(extraNumeralHeaders?.has(col))
            }
            frequencyOrdinal={needleOrdinalColumns?.has(col)}
            stripIndicator={belowFloor}
            lowSampleNote={
              lowSample
                ? `Fewer than ${CELL_SAMPLE_FLOORS[col].min} ${CELL_SAMPLE_FLOORS[col].unit}`
                : undefined
            }
            parts={parts}
            partDecimals={signed ?? (singleGame ? 1 : 2)}
          />
        );
      })}
      {showShotDistributionColumn && (
        <TableCell
          className="ShotDistributionCell"
          sx={{
            px: 0.5,
            py: 0,
            verticalAlign: "center",
          }}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <ShotDistributionBar
              data={row.shotDistribution}
              width={200}
              context={shotDistributionContext}
            />
            {row.queryContext &&
              (onShotDistributionClick || onVideoPlaylistClick) && (
                <Stack
                  direction="row"
                  sx={{ ml: 0.5, gap: "2px", flexShrink: 0 }}
                >
                  {onShotDistributionClick && (
                    <Tooltip title="Action detail" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={() =>
                          onShotDistributionClick(
                            row.queryContext!,
                            labelStr,
                            parentRowName,
                          )
                        }
                        sx={{
                          p: "2px",
                          color: "text.secondary",
                          opacity: 0.5,
                          "&:hover": { opacity: 1, color: "primary.main" },
                          transition: "opacity 0.15s, color 0.15s",
                        }}
                      >
                        <InsightsOutlined sx={{ fontSize: "1.208rem" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onVideoPlaylistClick && (
                    <Tooltip title="Video" arrow placement="top">
                      {videoLoadingKey === labelStr ? (
                        <Stack
                          sx={{
                            p: "2px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress
                            size={14}
                            sx={{ color: "text.secondary" }}
                          />
                        </Stack>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            onVideoPlaylistClick(
                              row.queryContext!,
                              labelStr,
                              parentRowName,
                              e.metaKey,
                            )
                          }
                          sx={{
                            p: "2px",
                            color: "text.secondary",
                            opacity: 0.5,
                            "&:hover": { opacity: 1, color: "primary.main" },
                            transition: "opacity 0.15s, color 0.15s",
                          }}
                        >
                          <PlayCircleOutline sx={{ fontSize: "1.208rem" }} />
                        </IconButton>
                      )}
                    </Tooltip>
                  )}
                </Stack>
              )}
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
}
